/** Shared /api/me client: dedupes concurrent callers and caches briefly. */

import { type InstanceModuleMap, normalizeModuleMap } from './instanceModules';

export type MeUser = {
  id: string;
  email: string;
  username: string;
};

export type MeInstance = {
  id: string;
  name?: string | null;
};

export type MeBilling = {
  state:
    | 'exempt'
    | 'active'
    | 'no_row'
    | 'no_subscription'
    | 'payment_overdue'
    | 'payment_overdue_urgent'
    | 'ending'
    | 'ended';
  until?: string | null;
};

export type MeResponse = {
  user?: MeUser;
  activeInstance?: MeInstance | null;
  instances?: MeInstance[];
  role?: 'owner' | 'admin' | 'member';
  role_preset_id?: string | null;
  capabilities?: Record<string, boolean>;
  /** Module der Instanz (src/lib/instanceModules.ts); abgewählte sind nirgends sichtbar. */
  modules?: Record<string, boolean>;
  companyName?: string | null;
  /** IANA zone for scheduling (company country, else Europe/Berlin). */
  timezone?: string;
  is_super_admin?: boolean;
  platform_access?: string;
  is_payroll_approver?: boolean;
  /** Present for owner and admin. Drives the billing notice. */
  billing?: MeBilling | null;
  [key: string]: unknown;
};

const DEFAULT_TTL_MS = 5_000;

let inflight: Promise<MeResponse | null> | null = null;
let cached: { at: number; value: MeResponse | null } | null = null;
/** Bumped on invalidate so in-flight responses cannot refill the cache after logout. */
let cacheEpoch = 0;
const invalidationListeners = new Set<() => void>();
/** Last auth failure code from `/api/me` (e.g. `"trial_ended"`). Cleared on success or invalidate. */
let lastMeAuthFailureCode: string | null = null;

function isFresh(ttlMs: number): boolean {
  // Only successful responses are cached; never treat a failed /me as "fresh".
  return Boolean(cached?.value && Date.now() - cached.at < ttlMs);
}

/** Drop cached /me (e.g. after logout or instance switch). */
export function invalidateMeCache(): void {
  cached = null;
  inflight = null;
  lastMeAuthFailureCode = null;
  cacheEpoch += 1;
  for (const listener of invalidationListeners) {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  }
}

/** Notify when /api/me cache is dropped (instance switch, logout, etc.). */
export function subscribeMeCacheInvalidation(listener: () => void): () => void {
  invalidationListeners.add(listener);
  return () => {
    invalidationListeners.delete(listener);
  };
}

/** Sync read of the last successful /api/me payload (may be stale beyond TTL). */
export function getCachedMe(): MeResponse | null {
  return cached?.value ?? null;
}

/** Sync capabilities for sidebar/settings so remounts do not flash all nav items. */
export function getCachedCapabilities(): Record<string, boolean> | null {
  const caps = cached?.value?.capabilities;
  return caps && typeof caps === 'object' ? { ...caps } : null;
}

/** Sync module map (Defaults plus /api/me); null, solange /api/me noch nie geantwortet hat. */
export function getCachedModules(): InstanceModuleMap | null {
  const value = cached?.value;
  if (!value) return null;
  return normalizeModuleMap(value.modules);
}

/**
 * Fetch /api/me once for concurrent callers. Returns null on network/auth failure.
 * Successful responses are short-TTL cached so remounts (Strict Mode, layout)
 * do not stampede the endpoint. Failures are not cached so post-login callers
 * can see the new session immediately.
 *
 * When the active demo is expired, returns null and sets
 * {@link getLastMeAuthFailureCode} to `"trial_ended"`.
 */
export class TrialEndedError extends Error {
  constructor(message = 'Dein Testzeitraum ist abgelaufen.') {
    super(message);
    this.name = 'TrialEndedError';
  }
}

export function getLastMeAuthFailureCode(): string | null {
  return lastMeAuthFailureCode;
}

export function clearLastMeAuthFailureCode(): void {
  lastMeAuthFailureCode = null;
}

export async function fetchMe(options?: { force?: boolean; ttlMs?: number }): Promise<MeResponse | null> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const force = options?.force === true;

  if (!force && isFresh(ttlMs)) {
    return cached!.value;
  }
  if (!force && inflight) {
    return inflight;
  }

  const startedEpoch = cacheEpoch;
  const request = (async () => {
    try {
      const res = await fetch('/api/me', { credentials: 'include' });
      if (startedEpoch !== cacheEpoch) return null;
      if (!res.ok) {
        // Drop any successful cache so a short TTL cannot revive an expired session.
        cached = null;
        const payload = (await res.json().catch(() => null)) as { code?: string; error?: string } | null;
        if (payload?.code === 'trial_ended') {
          lastMeAuthFailureCode = payload.code;
        } else {
          lastMeAuthFailureCode = null;
        }
        return null;
      }
      lastMeAuthFailureCode = null;
      const data = (await res.json()) as MeResponse;
      if (startedEpoch !== cacheEpoch) return null;
      cached = { at: Date.now(), value: data };
      return data;
    } catch {
      return null;
    } finally {
      if (inflight === request) inflight = null;
    }
  })();

  inflight = request;
  return request;
}
